import cn from 'classnames';
import { ComponentType, useEffect, useRef, useState } from 'react';
import type HeroSwiperT from './HeroSwiper';
import { generateContent } from './helpers';
import Main from './Main';
import Template from './Template';
import Arrows from './Arrows';
import { AUTOPLAY_DELAY } from './constants';
import settings from '@/settings';

type HeroSwiperProps = Parameters<typeof HeroSwiperT>[0];

// Swiper is expensive to start: it measures the page and forces a layout while
// the homepage hydrates. The hero first renders the same markup Swiper would
// for the first slide, and only loads Swiper when it's needed: when someone
// touches, hovers or focuses the hero, or when autoplay would have moved to the
// second slide.
const Hero = () => {
  const content = generateContent(settings.store);
  const [swiper, setSwiper] = useState<{
    Component: ComponentType<HeroSwiperProps>;
    initialSlide: number;
  }>();
  const loading = useRef(false);
  const targetSlide = useRef(0);

  const loadSwiper = () => {
    if (loading.current) {
      return;
    }

    loading.current = true;
    import('./HeroSwiper')
      .then(({ default: Component }) => {
        setSwiper({ Component, initialSlide: targetSlide.current });
      })
      .catch(() => {
        // The static first slide still works, so let the next trigger retry.
        loading.current = false;
      });
  };

  const goToSlide = (index: number) => {
    targetSlide.current = index;
    loadSwiper();
  };

  useEffect(() => {
    if (swiper) {
      return undefined;
    }

    let idleId: number | undefined;
    const timeout = window.setTimeout(() => {
      if (typeof window.requestIdleCallback === 'function') {
        idleId = window.requestIdleCallback(() => goToSlide(1), {
          timeout: 1000,
        });
      } else {
        goToSlide(1);
      }
    }, AUTOPLAY_DELAY);

    return () => {
      window.clearTimeout(timeout);

      if (idleId !== undefined) {
        window.cancelIdleCallback(idleId);
      }
    };
  }, [swiper]);

  if (swiper) {
    return (
      <swiper.Component content={content} initialSlide={swiper.initialSlide} />
    );
  }

  const slideCount = content.length + 1;

  return (
    <div
      className="relative"
      onPointerEnter={loadSwiper}
      onTouchStart={loadSwiper}
      onFocus={loadSwiper}
    >
      <Arrows
        theme="light"
        index={0}
        showNext={slideCount > 1}
        onNext={() => goToSlide(1)}
      />
      {/* The same classes Swiper puts on its own markup, so the hero doesn't
          move when Swiper replaces it. */}
      <div className="swiper hero-swiper light h-fit xxs:h-[280px]">
        <div className="swiper-wrapper">
          <div className="swiper-slide">
            <Main />
          </div>
          {content.map((item, idx) => (
            <div key={`hero-slide-${idx}`} className="swiper-slide">
              <Template {...item} />
            </div>
          ))}
        </div>
        <div className="swiper-pagination">
          {Array.from({ length: slideCount }, (_, idx) => (
            <button
              key={`hero-bullet-${idx}`}
              type="button"
              aria-label={`Go to slide ${idx + 1}`}
              className={cn('swiper-pagination-bullet', {
                'swiper-pagination-bullet-active': idx === 0,
              })}
              onClick={() => goToSlide(idx)}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default Hero;
